import React, { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import type { Viewer4dvProps, Viewer4dvElement } from '../types/viewer4dv'

const Viewer4dv: React.FC<Viewer4dvProps> = ({
  grid = true,
  stats = false,
  loop = true,
  srcMobile = "static/4ds/OnThisHill_R03_SNAP_15FPS_3kTris_MS4tx/OnThisHill_R03_SNAP_15FPS_3kTris_MS4tx_480p.4ds",
  srcDesktop = "static/4ds/OnThisHill_R03_SNAP_15FPS_3kTris_MS4tx/OnThisHill_R03_SNAP_15FPS_3kTris_MS4tx_480pDESKTOP.4ds",
  poster = "img/poster-soapbox-snap.webp",
  bgColor = "#000000",
  cameraPos = "[1,3,3]",
  cameraTarget = "[0,0.5,0]",
  minDistance = "2",
  maxDistance = "10",
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<Viewer4dvElement | null>(null)

  useEffect(() => {
    let isMounted = true

    const createViewer = async () => {
      if (!containerRef.current || viewerRef.current) return

      // Wait for custom element to be registered
      await customElements.whenDefined('viewer-4dv')

      // Double-check component is still mounted
      if (!isMounted || !containerRef.current) return

      // Build attribute string for boolean attributes
      const gridAttr = grid ? ' grid' : ''
      const statsAttr = stats ? ' stats' : ''
      const loopAttr = loop ? ' loop' : ''

      // Create element via innerHTML so attributes exist when constructor runs
      containerRef.current.innerHTML = `
        <viewer-4dv
          poster="${poster}"
          bg-color="${bgColor}"
          src-mobile="${srcMobile}"
          src-desktop="${srcDesktop}"
          camera-pos="${cameraPos}"
          camera-target="${cameraTarget}"
          min-distance="${minDistance}"
          max-distance="${maxDistance}"${gridAttr}${statsAttr}${loopAttr}
        ></viewer-4dv>
      `

      // Store reference to the created element
      viewerRef.current = containerRef.current.querySelector('viewer-4dv') as Viewer4dvElement
    }

    // Check if script is already loaded
    const existingScript = document.querySelector('script[src="https://unpkg.com/viewer-4dv/viewer-4dv.min.js"]')

    if (existingScript) {
      // Script already exists, wait for custom element definition
      createViewer()
    } else {
      // Load the viewer-4dv web component script
      const script = document.createElement('script')
      script.type = 'module'
      script.src = 'https://unpkg.com/viewer-4dv/viewer-4dv.min.js'
      script.defer = true

      const handleScriptLoad = () => {
        createViewer()
      }

      script.addEventListener('load', handleScriptLoad)
      document.head.appendChild(script)
    }

    return () => {
      isMounted = false

      // Cleanup viewer and free resources
      if (viewerRef.current) {
        try {
          // Call destroy method to stop playback and free resources
          if (typeof viewerRef.current.destroy === 'function') {
            console.log('Destroying viewer-4dv element and freeing resources')
            viewerRef.current.destroy()
          } else if (typeof viewerRef.current.pause === 'function') {
            // Fallback to pause if destroy is not available
            console.log('Pausing viewer-4dv element')
            viewerRef.current.pause()
          }
        } catch (e) {
          console.error('Error during viewer cleanup:', e)
        }

        // Remove element from DOM
        if (containerRef.current) {
          try {
            containerRef.current.removeChild(viewerRef.current)
          } catch (e) {
            // Element might already be removed
            console.warn('Element already removed from DOM')
          }
        }

        viewerRef.current = null
      }
    }
  }, [
    grid,
    stats,
    loop,
    srcMobile,
    srcDesktop,
    poster,
    bgColor,
    cameraPos,
    cameraTarget,
    minDistance,
    maxDistance,
  ])

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        '& viewer-4dv': {
          width: '100%',
          height: '100%',
          display: 'block',
        },
      }}
    />
  )
}

export default Viewer4dv